/**
 * Utility functions for handling text encoding issues
 * Particularly useful for French text that may have been encoded as ISO-8859-1
 */

/**
 * Fix common encoding issues where UTF-8 characters are displayed incorrectly
 * e.g., "VidÃ©o" should be "Vidéo"
 */
export const fixEncodingIssues = (text: string): string => {
  if (!text) return text;
  
  // Common UTF-8 bytes misread as ISO-8859-1. Keep this table ASCII-only
  // using Unicode escapes so it still works on dealer sites with ISO-8859-1 pages.
  const encodingFixes: Record<string, string> = {
    '\u00c3\u0192\u00c2\u00a9': '\u00e9',
    '\u00c3\u0192\u00c2\u00a8': '\u00e8',
    '\u00c3\u0192\u00c2\u00aa': '\u00ea',
    '\u00c3\u0192\u00c2\u00a0': '\u00e0',
    '\u00c3\u0192\u00c2\u00a7': '\u00e7',
    '\u00c3\u0192\u00c2\u00b4': '\u00f4',
    '\u00c3\u0192\u00c2\u00bb': '\u00fb',
    '\u00c3\u0192\u00c2\u0089': '\u00c9',
    '\u00c3\u00a9': '\u00e9',
    '\u00c3\u00a8': '\u00e8',
    '\u00c3\u00aa': '\u00ea',
    '\u00c3\u00b4': '\u00f4',
    '\u00c3\u00a2': '\u00e2',
    '\u00c3\u00a0': '\u00e0',
    '\u00c3\u00a7': '\u00e7',
    '\u00c3\u00b9': '\u00f9',
    '\u00c3\u00bb': '\u00fb',
    '\u00c3\u00af': '\u00ef',
    '\u00c3\u00ae': '\u00ee',
    '\u00c3\u00ab': '\u00eb',
    '\u00c3\u0080': '\u00c0',
    '\u00c3\u0089': '\u00c9',
    '\u00c3\u0088': '\u00c8',
    '\u00c3\u008a': '\u00ca',
    '\u00c3\u0094': '\u00d4',
    '\u00c3\u0082': '\u00c2',
    '\u00c3\u0087': '\u00c7',
    '\u00c3\u0099': '\u00d9',
    '\u00c3\u009b': '\u00db',
    '\u00c3\u008e': '\u00ce',
    '\u00c3\u008b': '\u00cb',
    '\u00c2\u00a0': ' ',
  };

  let fixedText = text;
  
  // Replace all encoding issues - use split/join to avoid regex escaping issues
  for (const [wrong, correct] of Object.entries(encodingFixes)) {
    fixedText = fixedText.split(wrong).join(correct);
  }
  
  return fixedText;
};

/**
 * Decode text that may have been double-encoded or incorrectly encoded
 * Uses both HTML entity decoding and ISO-8859-1 fix mappings
 */
export const decodeText = (text: string): string => {
  if (!text) return text;
  
  try {
    // Step 1: Decode HTML entities using DOMParser (safe from XSS)
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${text}</div>`, 'text/html');
    let decodedText = doc.body.textContent || doc.body.innerText || text;
    
    // Step 2: Fix ISO-8859-1 encoding issues
    decodedText = fixEncodingIssues(decodedText);
    
    // Step 3: Check for double-encoding and fix again if needed
    if (decodedText.includes('Ã')) {
      decodedText = fixEncodingIssues(decodedText);
    }
    
    return decodedText;
  } catch (error) {
    console.error('Error decoding text:', error);
    // Fallback: at least try to fix encoding issues
    return fixEncodingIssues(text);
  }
};
